import {
  isAwaitingBatchEditInstruction,
  isAwaitingBatchRejectInstruction,
  isCampaignPhaseStarted,
  isCampaignWidgetInputLocked,
  isChatAsyncProcessingLocked,
  isChatInputLocked,
  isProcessing as resolveIsProcessing,
  isSessionReadOnly,
  resolveChatAttachMode,
  resolveScheduleUxState,
  hasArtToolActivityInMessages,
  getStrategyMarkdown,
  resolveSessionPreviewData,
  shouldShowCampaignCanvas,
} from './campaignStateSelectors';
import {reconcileLiveOverlay} from './campaignLiveOverlay';
import {
  resolveLatestInteraction,
  resolveActiveOperationIdentity,
  findToolPartByTrackingId,
  findMessageIdByTrackingId,
  findToolPart,
  getArtResultsFromMessages,
  isFullStrategyPipelineToolType,
  isStrategyDocumentOnlyToolType,
} from './toolPartSelectors';
import {CAMPAIGN_WIDGET_TOOL_NAMES} from '../utils/campaignCompletionMessages';
import {
  CAMPAIGN_TOOL_CATALOG,
  isToolInputLocked,
} from './campaignToolCatalog';
import {MESSAGE_INTEGRATIONS} from '../utils/messageIntegrations.config';
import {
  buildDocumentOnlyStrategySteps,
  buildQueueStepsFromProgress,
  buildRegenStrategyQueueStepsFromDefaults,
} from '../hooks/campaignSocket/constants';
import {resolvePendingStrategyApprovalIntegration} from '../utils/messageIntegrationHelpers';
import {getCampaignArtProgressTitle} from './campaignArtProgress';

function resolveArtProgressTitle({
  artStatus,
  operation,
  sessionStatus,
  generatingTitle,
  editingTitle,
}) {
  if (operation?.stepName && operation.status === 'processing') {
    if (
      operation.toolName === 'editar_arte_campanha' ||
      operation.toolName === 'editar_artes_lote'
    ) {
      return operation.stepName;
    }
  }

  return getCampaignArtProgressTitle(artStatus, sessionStatus, () =>
    artStatus === 'editing' ? editingTitle : generatingTitle,
  );
}

function isTerminalOperationStatus(status) {
  return status === 'completed' || status === 'error';
}

const STRATEGY_OPERATION_TOOLS = new Set([
  'gerar_documento_estrategia',
  'editar_documento_estrategia',
  'regerar_documento_estrategia',
]);

const ART_OPERATION_TOOLS = new Set([
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
]);

const ART_GENERATION_TOOL_NAMES = new Set([
  'gerar_artes_campanha',
  'regerar_artes_campanha',
]);

const ART_BATCH_EDIT_TOOL_NAMES = new Set(['editar_artes_lote']);
const ART_SINGLE_EDIT_TOOL_NAMES = new Set(['editar_arte_campanha']);

function getOperationFamily(toolName) {
  if (STRATEGY_OPERATION_TOOLS.has(toolName)) {
    return 'strategy';
  }

  if (ART_OPERATION_TOOLS.has(toolName)) {
    return 'arts';
  }

  if (toolName === 'executar_agendamento') {
    return 'scheduling';
  }

  if (toolName) {
    return 'finalization';
  }

  return null;
}

function resolveAttachMode(interaction) {
  if (!interaction || interaction.status === 'submitting') {
    return null;
  }
  const entry = CAMPAIGN_TOOL_CATALOG.find(
    (item) => item.toolName === interaction.toolName,
  );
  return entry?.attachMode ?? null;
}

function resolveInteraction(messages, pendingSubmissions = {}) {
  return resolveLatestInteraction(
    messages,
    CAMPAIGN_TOOL_CATALOG,
    pendingSubmissions,
  );
}

function getOverlayOperation(liveOverlay, sessionId, trackingId) {
  if (!trackingId || !liveOverlay?.operations) {
    return null;
  }

  const key = sessionId ? `${sessionId}:${trackingId}` : null;
  if (key && liveOverlay.operations[key]) {
    return liveOverlay.operations[key];
  }

  return (
    Object.values(liveOverlay.operations).find(
      (op) => op.trackingId === trackingId && op.status === 'processing',
    ) ?? null
  );
}

export function resolveActiveOperation(messages, liveOverlay, snapshot = null) {
  const {trackingId, part, snapshotOp} = resolveActiveOperationIdentity(
    messages,
    snapshot,
    liveOverlay,
  );

  if (trackingId) {
    const terminalPart = findToolPartByTrackingId(messages, trackingId);
    if (terminalPart && isTerminalOperationStatus(terminalPart.output?.status)) {
      return null;
    }
  }

  const sessionId = snapshot?.sessionId ?? null;
  const overlayOp =
    trackingId && liveOverlay
      ? getOverlayOperation(liveOverlay, sessionId, trackingId)
      : null;

  let toolName = null;
  let currentStep = 0;
  let totalSteps = 0;
  let stepName = '';
  let status = 'processing';
  let partType = null;
  let activeTrackingId = trackingId;
  let pieceNumber = null;

  if (part?.output?.status === 'processing') {
    toolName =
      part.output.toolName ??
      (part.type?.startsWith('tool-') ? part.type.slice('tool-'.length) : null);
    currentStep = part.output.currentStep ?? 0;
    totalSteps = part.output.totalSteps ?? 0;
    stepName = part.output.stepName ?? '';
    status = part.output.status;
    partType = part.type;
    pieceNumber =
      typeof part.output.pieceNumber === 'number'
        ? part.output.pieceNumber
        : null;
    activeTrackingId = part.output.trackingId || part.toolCallId || trackingId;
  } else if (snapshotOp?.status === 'processing') {
    toolName = snapshotOp.toolName ?? null;
    currentStep = snapshotOp.currentStep ?? 0;
    totalSteps = snapshotOp.totalSteps ?? 0;
    stepName = snapshotOp.stepName ?? '';
    status = snapshotOp.status;
    partType = toolName ? `tool-${toolName}` : null;
    activeTrackingId = snapshotOp.trackingId ?? trackingId;
  } else if (overlayOp) {
    toolName = overlayOp.toolName ?? null;
    currentStep = overlayOp.currentStep ?? 0;
    totalSteps = overlayOp.totalSteps ?? 0;
    stepName = overlayOp.stepName ?? '';
    status = overlayOp.status ?? 'processing';
    partType = toolName ? `tool-${toolName}` : null;
    activeTrackingId = overlayOp.trackingId ?? trackingId;
  } else {
    return null;
  }

  if (overlayOp && overlayOp.trackingId === activeTrackingId) {
    const overlayStep = overlayOp.currentStep ?? 0;
    if (overlayStep > currentStep) {
      currentStep = overlayStep;
      stepName = overlayOp.stepName ?? stepName;
    }
    totalSteps = Math.max(totalSteps, overlayOp.totalSteps ?? 0);
  }

  if (status !== 'processing') {
    return null;
  }

  return {
    toolName,
    family: getOperationFamily(toolName),
    currentStep,
    totalSteps,
    stepName,
    status,
    trackingId: activeTrackingId,
    partType,
    pieceNumber,
  };
}

function resolveArtProgress(operation) {
  if (operation && operation.family === 'arts') {
    const current = operation.currentStep ?? 0;
    const total = operation.totalSteps ?? 0;
    return {
      current,
      total,
      progress: total > 0 ? Math.round((current / total) * 100) : 0,
    };
  }

  return {
    current: 0,
    total: 0,
    progress: 0,
  };
}

function resolveArtStatus(messages, liveOverlay, operation) {
  if (operation?.family === 'arts' && operation.status === 'processing') {
    if (
      operation.toolName === 'editar_arte_campanha' ||
      operation.toolName === 'editar_artes_lote'
    ) {
      return 'editing';
    }
    return 'generating';
  }

  const artsFromMessages = getArtResultsFromMessages(messages);
  if (artsFromMessages.length > 0) {
    return 'generated';
  }

  if (hasArtToolActivityInMessages(messages) || liveOverlay?.arts) {
    return 'idle';
  }

  return 'idle';
}

function resolveStrategyProgress(
  operation,
  queueStepsTemplate = [],
  strategyDocumentEditTitle = 'Editando documento de estratégia...',
  strategyRegenQueueStepsTemplate = [],
) {
  if (
    !operation ||
    operation.family !== 'strategy' ||
    operation.status !== 'processing'
  ) {
    return {
      visible: false,
      steps: [],
    };
  }

  if (isFullStrategyPipelineToolType(operation.partType)) {
    return {
      visible: true,
      steps: buildQueueStepsFromProgress(
        operation.currentStep,
        queueStepsTemplate,
      ),
    };
  }

  if (operation.toolName === 'editar_documento_estrategia') {
    return {
      visible: true,
      steps: buildDocumentOnlyStrategySteps(strategyDocumentEditTitle, 'pending'),
    };
  }

  if (operation.toolName === 'regerar_documento_estrategia') {
    const baseSteps =
      strategyRegenQueueStepsTemplate.length > 0
        ? strategyRegenQueueStepsTemplate
        : buildRegenStrategyQueueStepsFromDefaults();
    return {
      visible: true,
      steps: buildQueueStepsFromProgress(operation.currentStep, baseSteps),
    };
  }

  if (isStrategyDocumentOnlyToolType(operation.partType)) {
    return {
      visible: true,
      steps: buildDocumentOnlyStrategySteps(
        operation.stepName || strategyDocumentEditTitle,
        'pending',
      ),
    };
  }

  return {
    visible: true,
    steps: buildDocumentOnlyStrategySteps(
      operation.stepName || strategyDocumentEditTitle,
      'pending',
    ),
  };
}

function resolveArtSteps(
  artProgress,
  arts,
  formatArtStepLabel,
  operation,
  artStatus,
) {
  const isActiveArtOperation =
    artStatus === 'generating' || artStatus === 'editing';
  const isBatchEdit = ART_BATCH_EDIT_TOOL_NAMES.has(operation?.toolName);
  const isSingleEdit = ART_SINGLE_EDIT_TOOL_NAMES.has(operation?.toolName);
  const isGeneration = ART_GENERATION_TOOL_NAMES.has(operation?.toolName);
  const total = isSingleEdit
    ? 1
    : Math.max(artProgress.total || 0, arts.length);

  if (!total) {
    return [];
  }

  if (isActiveArtOperation) {
    if (isBatchEdit) {
      const completedPieces = Math.max(0, artProgress.current || 0);

      return Array.from({length: total}, (_, index) => {
        const pieceNumber = index + 1;
        let status = 'idle';

        if (pieceNumber <= completedPieces) {
          status = 'complete';
        } else if (pieceNumber === completedPieces + 1) {
          status = 'pending';
        }

        return {
          text: formatArtStepLabel(pieceNumber),
          status,
        };
      });
    }

    if (isSingleEdit) {
      const pieceNumber = operation?.pieceNumber ?? 1;
      return [
        {
          text: formatArtStepLabel(pieceNumber),
          status: 'pending',
        },
      ];
    }

    if (isGeneration) {
      const completedPieces = Math.max(0, artProgress.current || 0);

      return Array.from({length: total}, (_, index) => {
        const pieceNumber = index + 1;
        if (pieceNumber <= completedPieces) {
          return {text: formatArtStepLabel(pieceNumber), status: 'complete'};
        }
        if (pieceNumber === completedPieces + 1) {
          return {text: formatArtStepLabel(pieceNumber), status: 'pending'};
        }
        return {text: formatArtStepLabel(pieceNumber), status: 'idle'};
      });
    }
  }

  return [];
}

function buildCampaignPresentation({
  messages,
  operation,
  strategyQueueStepsTemplate,
  strategyRegenQueueStepsTemplate,
  strategyProgressTitle,
  strategyDocumentEditTitle,
  artSteps,
  artProgressTitle,
}) {
  const byMessageId = {};
  const floating = [];

  if (!operation || operation.status !== 'processing') {
    return {byMessageId, floating};
  }

  const trackingId = operation.trackingId;
  const messageId = findMessageIdByTrackingId(messages, trackingId);
  const strategyProgress = resolveStrategyProgress(
    operation,
    strategyQueueStepsTemplate,
    strategyDocumentEditTitle,
    strategyRegenQueueStepsTemplate,
  );

  if (operation.family === 'strategy' && strategyProgress.visible) {
    const progressTitle =
      operation.toolName === 'editar_documento_estrategia'
        ? strategyDocumentEditTitle
        : strategyProgressTitle;
    const integration = {
      key: 'strategyProgress',
      trackingId,
      props: {
        steps: strategyProgress.steps,
        title: progressTitle,
        progress: {
          current: operation.currentStep ?? 0,
          total: operation.totalSteps ?? 0,
          label: operation.stepName ?? '',
          steps: strategyProgress.steps.map((step, index) => ({
            id: String(index),
            label: step.text,
            status: step.status,
          })),
        },
      },
    };

    if (messageId) {
      byMessageId[messageId] = [integration];
    } else {
      floating.push(integration);
    }
    return {byMessageId, floating};
  }

  if (
    operation.family === 'arts' &&
    !findToolPart(messages, CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES, {
      statuses: ['awaiting_selection'],
    }) &&
    Array.isArray(artSteps) &&
    artSteps.length > 0
  ) {
    const integration = {
      key: 'artQueueProgress',
      trackingId,
      props: {
        steps: artSteps,
        title: artProgressTitle,
        progress: {
          current: operation.currentStep ?? 0,
          total: operation.totalSteps ?? 0,
          label: operation.stepName ?? '',
          steps: artSteps.map((step, index) => ({
            id: String(index + 1),
            label: step.text,
            status: step.status,
          })),
        },
      },
    };

    if (messageId) {
      byMessageId[messageId] = [integration];
    } else {
      floating.push(integration);
    }
  }

  return {byMessageId, floating};
}

function mapLiveOverlayArts(liveOverlay) {
  if (!liveOverlay?.arts) {
    return [];
  }
  return Object.values(liveOverlay.arts);
}

function mergeOverlayArts(messageArts, overlayArts) {
  if (!overlayArts.length) {
    return messageArts;
  }

  const merged = [...messageArts];
  overlayArts.forEach((overlayArt) => {
    const pieceNumber = overlayArt.pieceNumber;
    if (pieceNumber == null) {
      return;
    }
    const index = merged.findIndex((art) => art.pieceNumber === pieceNumber);
    const nextArt = {
      pieceNumber,
      url: overlayArt.imageUrl ?? overlayArt.url,
      imageUrl: overlayArt.imageUrl ?? overlayArt.url,
      revision: overlayArt.revision ?? null,
      trackingId: overlayArt.trackingId ?? overlayArt.toolCallId ?? null,
    };
    if (index >= 0) {
      merged[index] = {...merged[index], ...nextArt};
    } else {
      merged.push(nextArt);
    }
  });
  return merged.sort((a, b) => (a.pieceNumber ?? 0) - (b.pieceNumber ?? 0));
}

export function projectCampaignRuntime({
  snapshot = null,
  sessionDetail = null,
  transcript = [],
  liveOverlay = null,
  pendingSubmissions = {},
  chatTransportStatus = 'idle',
  localDraft = {},
  strategyQueueStepsTemplate = [],
  strategyRegenQueueStepsTemplate = [],
  formatArtStepLabel = (pieceNumber) => `Arte ${pieceNumber}`,
  strategyProgressTitle = 'Gerando estratégia...',
  strategyDocumentEditTitle = 'Editando documento de estratégia...',
  artProgressTitle = 'Gerando artes...',
  artProgressEditingTitle = 'Editando artes...',
}) {
  const snapshotMessages = snapshot?.messages ?? [];
  const transcriptMessages = Array.isArray(transcript) ? transcript : [];
  const messages = transcriptMessages.length > 0 ? transcriptMessages : snapshotMessages;
  const resolvedLiveOverlay = reconcileLiveOverlay(liveOverlay, {snapshot, messages});
  const session = {
    id: snapshot?.sessionId ?? null,
    revision: snapshot?.revision ?? null,
    status: snapshot?.status ?? 'active',
    phase: snapshot?.workflow?.phase ?? null,
  };

  const strategyFromSession = snapshot?.strategy ?? '';
  const operation = resolveActiveOperation(messages, resolvedLiveOverlay, snapshot);
  const interaction = resolveInteraction(messages, pendingSubmissions);
  const attachMode = resolveAttachMode(interaction);

  const artProgress = resolveArtProgress(operation);
  const artStatus = resolveArtStatus(messages, resolvedLiveOverlay, operation);
  const overlayArts = mapLiveOverlayArts(resolvedLiveOverlay);
  const messageArts = getArtResultsFromMessages(messages);
  const arts = mergeOverlayArts(messageArts, overlayArts);
  const strategyProgress = resolveStrategyProgress(
    operation,
    strategyQueueStepsTemplate,
    strategyDocumentEditTitle,
    strategyRegenQueueStepsTemplate,
  );

  const preview = resolveSessionPreviewData({
    messages,
    strategyFromSession,
    strategyMarkdown: '',
    artResults: arts,
    artStatus,
    artProgress,
    sessionDetail: sessionDetail ?? snapshot,
  });

  const strategy = getStrategyMarkdown(messages, {
    socketMarkdown: '',
    sessionMarkdown: strategyFromSession,
  });
  const showCanvas = shouldShowCampaignCanvas({
    messages,
    strategyFromSession,
    strategyMarkdown: strategy,
  });

  const artSteps = resolveArtSteps(
    preview.artProgress ?? artProgress,
    preview.artResults ?? arts,
    formatArtStepLabel,
    operation,
    artStatus,
  );
  const resolvedArtProgressTitle = resolveArtProgressTitle({
    artStatus,
    operation,
    sessionStatus: session.status,
    generatingTitle: artProgressTitle,
    editingTitle: artProgressEditingTitle,
  });
  const pendingWidgetLocked = Boolean(
    interaction &&
      interaction.status === 'awaiting_selection' &&
      isToolInputLocked(interaction.toolName),
  );
  const sessionReadOnly = isSessionReadOnly(snapshot);
  const inputLockedByProcessing = isChatAsyncProcessingLocked(messages, {
    socketProcessing: Boolean(operation),
    processingToolName: operation?.toolName ?? null,
    campaignStatus: session.status,
  });

  const inputReason = sessionReadOnly
    ? 'session_completed'
    : pendingWidgetLocked
      ? 'awaiting_widget'
      : inputLockedByProcessing
        ? 'processing'
        : chatTransportStatus === 'submitted' ||
          chatTransportStatus === 'streaming'
          ? 'submitting'
          : null;

  const batchEditMode = isAwaitingBatchEditInstruction(messages);
  const batchRejectMode = isAwaitingBatchRejectInstruction(messages);

  const input = {
    disabled: Boolean(inputReason),
    reason: inputReason,
    attachMode,
    batchEditMode,
    batchRejectMode,
  };

  let presentation = buildCampaignPresentation({
    messages,
    operation,
    strategyQueueStepsTemplate,
    strategyRegenQueueStepsTemplate,
    strategyProgressTitle,
    strategyDocumentEditTitle,
    artSteps,
    artProgressTitle: resolvedArtProgressTitle,
  });

  const pendingStrategyApproval = resolvePendingStrategyApprovalIntegration({
    messages,
    sessionMessages: snapshotMessages,
    campaignStatus: session.status,
  });
  if (pendingStrategyApproval) {
    presentation = {
      ...presentation,
      floating: [...presentation.floating, pendingStrategyApproval],
    };
  }

  const canvas = {
    visible: showCanvas,
    activeTabAvailable: Boolean(strategy || arts.length > 0),
  };

  return {
    session,
    interaction,
    operation,
    content: {
      strategy,
      arts: preview.artResults ?? arts,
      artStatus,
      artProgress,
    },
    input,
    presentation,
    canvas,
    strategyProgress,
    artSteps,
    processing: {
      isProcessing: resolveIsProcessing(messages, {
        socketProcessing: Boolean(operation),
      }),
      pendingSubmissions,
    },
    preview,
    snapshot,
    liveOverlay: resolvedLiveOverlay,
    localDraft,
  };
}

export function selectCampaignViewModel({
  snapshot = null,
  sessionDetail = null,
  transcript = [],
  liveOverlay = null,
  pendingSubmissions = {},
  localDraft = {},
  strategyQueueStepsTemplate = [],
  strategyRegenQueueStepsTemplate = [],
  formatArtStepLabel = (pieceNumber) => `Arte ${pieceNumber}`,
  chatTransportStatus = 'idle',
  strategyProgressTitle = 'Gerando estratégia...',
  strategyDocumentEditTitle = 'Editando documento de estratégia...',
  artProgressTitle = 'Gerando artes...',
  artProgressEditingTitle = 'Editando artes...',
}) {
  const runtime = projectCampaignRuntime({
    snapshot,
    sessionDetail,
    transcript,
    liveOverlay,
    pendingSubmissions,
    chatTransportStatus,
    localDraft,
    strategyQueueStepsTemplate,
    strategyRegenQueueStepsTemplate,
    formatArtStepLabel,
    strategyProgressTitle,
    strategyDocumentEditTitle,
    artProgressTitle,
    artProgressEditingTitle,
  });

  return {
    isProcessing: runtime.processing.isProcessing,
    processingToolName: runtime.operation?.toolName ?? null,
    isChatInputLocked: runtime.input.disabled,
    attachMode: runtime.input.attachMode,
    strategy: runtime.content.strategy,
    arts: runtime.content.arts,
    artStatus: runtime.content.artStatus,
    artProgress: runtime.content.artProgress,
    artSteps: runtime.artSteps,
    showArtsTab: runtime.preview.showArtsTab,
    strategyProgress: runtime.strategyProgress,
    messageIntegrations: MESSAGE_INTEGRATIONS,
    showCanvas: runtime.canvas.visible,
    isReadOnly: runtime.session.status === 'completed',
    pendingInteraction: runtime.interaction,
    pendingSubmissions,
    localDraft,
    revision: runtime.session.revision,
    workflow: snapshot?.workflow ?? null,
    liveOverlay,
    runtime,
  };
}
